// What this browser uploads in a meeting, per peer connection and in total (FPT-7). The meeting is a mesh, so
// upload is the number that limits how many people a meeting can hold on a given connection; this is how the
// real limit is measured. Pure (no imports) so `npm test` covers it; `useUploadLog` in MeetingRoom drives it.
//
// Turn the console log on in the meeting tab's devtools:
//     localStorage.setItem("fg.connect.meeting.uploadLog", "1")   // then reload the meeting
// Every 5 s it prints a table (kbps per peer: audio, video, total) and keeps the latest sample on
// `window.__fgMeetingUpload` for scripts. `localStorage.removeItem(...)` turns it off. The "Call quality" panel
// (More → Call quality) shows the same per-peer upload and the total, live.

export const UPLOAD_LOG_KEY = "fg.connect.meeting.uploadLog";

/** Bytes sent so far on one peer connection, by media kind (screen share counts as video). */
export interface OutboundBytes {
    audio: number;
    video: number;
}

/** Sums the `outbound-rtp` entries of one `RTCPeerConnection.getStats()` report. */
export function outboundBytes(report: Iterable<Record<string, unknown>>): OutboundBytes {
    const out: OutboundBytes = { audio: 0, video: 0 };
    for (const s of report) {
        if (s["type"] !== "outbound-rtp") continue;
        const kind = (s["kind"] ?? s["mediaType"]) as unknown;
        const bytes = typeof s["bytesSent"] === "number" ? s["bytesSent"] as number : 0;
        const header = typeof s["headerBytesSent"] === "number" ? s["headerBytesSent"] as number : 0;
        if (kind === "audio") out.audio += bytes + header;
        else if (kind === "video") out.video += bytes + header;
    }
    return out;
}

export interface UploadRow {
    peer: string;
    audioKbps: number;
    videoKbps: number;
    totalKbps: number;
}

/** kbps between two samples taken `seconds` apart (never negative: a rebuilt connection restarts its counters). */
export function uploadRow(peer: string, prev: OutboundBytes | undefined, now: OutboundBytes, seconds: number): UploadRow {
    const kbps = (a: number, b: number) => (seconds > 0 ? Math.max(0, Math.round((b - a) * 8 / 1000 / seconds)) : 0);
    const audioKbps = prev ? kbps(prev.audio, now.audio) : 0;
    const videoKbps = prev ? kbps(prev.video, now.video) : 0;
    return { peer, audioKbps, videoKbps, totalKbps: audioKbps + videoKbps };
}

/** One total row over every peer. */
export function uploadTotal(rows: readonly UploadRow[]): UploadRow {
    return rows.reduce<UploadRow>((t, r) => ({
        peer: "TOTAL",
        audioKbps: t.audioKbps + r.audioKbps,
        videoKbps: t.videoKbps + r.videoKbps,
        totalKbps: t.totalKbps + r.totalKbps
    }), { peer: "TOTAL", audioKbps: 0, videoKbps: 0, totalKbps: 0 });
}
